import { TrackingPropsInterface } from '@marriott/mi-ui-library';
import { isExternalUrl } from './index';

/**
 * Common tracking configuration interface
 * Used across all Account Refresh components
 */
export type CommonTrackingConfig = {
  keyValue?: string; // Optional key for lookup (e.g., 'buyButton', 'earnButton')
  trackingContentPosition?: string;
  trackingDescription?: string;
  clickTrack?: boolean;
  impressionTrack?: boolean;
  impressionCount?: boolean;
  linkType?: string;
};

/**
 * Finds a tracking configuration from the tracking array by keyValue
 * Following Gift Card and Upcoming Trips pattern
 *
 * @param tracking - Array of tracking configurations from AEM model
 * @param keyValue - The keyValue to search for (e.g., 'buyButton', 'viewInProfileButton')
 * @returns The matching tracking config or undefined
 *
 * @example
 * const config = findTrackingConfig(model.tracking, 'buyButton');
 * const tracking = mapTrackingProperties(config);
 */
export const findTrackingConfig = (
  tracking: CommonTrackingConfig[] | undefined,
  keyValue: string
): CommonTrackingConfig | undefined => {
  if (!tracking || !Array.isArray(tracking)) {
    return undefined;
  }

  return tracking.find(config => config.keyValue === keyValue);
};

const normalizeTrackingKey = (value?: string): string => (value ?? '').trim().toLowerCase();

/**
 * Finds the first tracking row whose keyValue matches any of the given keys.
 * Comparison is trimmed and case-insensitive so AEM "Buy" matches "buyButton" aliases.
 * Returns undefined when tracking or keyValues is empty, or when nothing matches.
 */
export const findTrackingConfigByKeys = (
  tracking: CommonTrackingConfig[] | undefined,
  keyValues: string[]
): CommonTrackingConfig | undefined => {
  if (!tracking || !Array.isArray(tracking) || keyValues.length === 0) {
    return undefined;
  }

  const wanted = new Set(keyValues.map(normalizeTrackingKey).filter(Boolean));
  return tracking.find(config => wanted.has(normalizeTrackingKey(config.keyValue)));
};

/**
 * Convenience function that combines finding and mapping tracking config
 *
 * @param tracking - Array of tracking configurations from AEM model
 * @param keyValue - The keyValue to search for
 * @returns TrackingPropsInterface ready to pass to Button/Link component
 *
 * @example
 * const buyTracking = getTrackingByKey(model.tracking, 'buyButton');
 * <Button trackingProperties={buyTracking} />
 */
export const getTrackingByKey = (
  tracking: CommonTrackingConfig[] | undefined,
  keyValue: string
): TrackingPropsInterface => {
  const config = findTrackingConfig(tracking, keyValue);
  return mapTrackingProperties(config);
};

/**
 * Maps tracking config to TrackingPropsInterface
 *
 * **Shared utility for all Account Refresh components:**
 * - Earnings Summary
 * - Benefits Module & Modal
 * - Member Details
 * - Inline Messages
 * - Gift Card
 * - Upcoming Trips
 *
 * Safely handles undefined/missing values - returns empty object if tracking data is incomplete
 *
 * @param config - Tracking configuration object with optional tracking fields
 * @returns TrackingPropsInterface object for Button/Link components, or empty object if invalid
 *
 * @example
 * // From AEM tracking array
 * const tracking = mapTrackingProperties(model.tracking[0]);
 * <Link trackingProperties={tracking} />
 *
 * @example
 * // From embedded tracking in object
 * const tracking = mapTrackingProperties(shopCard.tracking);
 * <Button trackingProperties={tracking} />
 */
export const mapTrackingProperties = (config?: CommonTrackingConfig): TrackingPropsInterface => {
  if (!config) {
    return {};
  }

  // Safety check: Don't enable tracking if required fields are missing
  // This prevents "undefined|undefined|internal" in analytics
  if (!config.trackingContentPosition || !config.trackingDescription) {
    return {};
  }

  return {
    trackingContentPosition: config.trackingContentPosition,
    trackingDescription: config.trackingDescription,
    clickTrack: config.clickTrack,
    impressionTrack: false, // Always false for buttons/links
    impressionCount: false, // Always false - Adobe handles this
  };
};

/**
 * Creates custom click track value string for plain HTML elements
 * Following Gift Card pattern from ViewGiftCard/__utils__/index.ts line 65-74
 *
 * Used when tracking is added via data-custom_click_track_value attribute
 * instead of through Link/Button components
 *
 * @param config - Tracking configuration
 * @param linkType - Type of link: 'internal' (default) or 'external'
 * @returns Custom click track value string or undefined
 *
 * @example
 * const customTrackValue = getCustomClickTrackValues(trackingProps);
 * <div data-custom_click_track_value={customTrackValue} onClick={...} />
 *
 * @example
 * // For external links
 * const customTrackValue = getCustomClickTrackValues(trackingProps, 'external');
 */
export const getCustomClickTrackValues = (
  config: TrackingPropsInterface | undefined,
  linkType: 'internal' | 'external' = 'internal'
) => {
  const { trackingContentPosition, trackingDescription } = config || {};

  if (!trackingContentPosition && !trackingDescription) {
    return undefined;
  }
  const parts = [trackingContentPosition, trackingDescription && `${trackingDescription}|${linkType}`].filter(Boolean);

  return parts.join('|') || undefined;
};

/**
 * Convenience function that combines finding tracking config and creating custom click track value
 * This reduces the need for multiple useMemo calls when both tracking props and custom track values are needed
 *
 * @param tracking - Array of tracking configurations from AEM model
 * @param keyValue - The keyValue to search for
 * @returns Object containing both trackingProperties and customClickTrackValue
 *
 * @example
 * const { trackingProperties, customClickTrackValue } = getTrackingWithCustomClickValue(model.tracking, 'buyButton');
 * <Button trackingProperties={trackingProperties} custom_click_track_value={customClickTrackValue} />
 */
export const getTrackingWithCustomClickValue = (
  tracking: CommonTrackingConfig[] | undefined,
  keyValue: string
): { trackingProperties: TrackingPropsInterface; customClickTrackValue: string | undefined } => {
  const trackingProperties = getTrackingByKey(tracking, keyValue);
  const customClickTrackValue = getCustomClickTrackValues(trackingProperties);
  return { trackingProperties, customClickTrackValue };
};

/**
 * Creates custom click track value with automatic internal/external detection based on URL
 * Uses isExternalUrl utility to determine link type
 *
 * @param config - Tracking configuration
 * @param url - The URL to check for internal/external
 * @returns Custom click track value string or undefined
 *
 * @example
 * const customTrack = getCustomClickTrackValueFromUrl(trackingProps, 'https://external-site.com');
 * // Returns: "Upcoming Trip|Plan For Your Stay Button|external"
 *
 * @example
 * const customTrack = getCustomClickTrackValueFromUrl(trackingProps, '/internal/path');
 * // Returns: "Upcoming Trip|View/Modify Button|internal"
 */
export const getCustomClickTrackValueFromUrl = (
  config: TrackingPropsInterface | undefined,
  url: string | undefined
): string | undefined => {
  const linkType = url && isExternalUrl(url) ? 'external' : 'internal';
  return getCustomClickTrackValues(config, linkType);
};

/**
 * Convenience function that combines finding tracking config and creating custom click track value
 * with automatic internal/external detection based on URL
 *
 * @param tracking - Array of tracking configurations from AEM model
 * @param keyValue - The keyValue to search for
 * @param url - The URL to check for internal/external
 * @returns Object containing both trackingProperties and customClickTrackValue
 *
 * @example
 * const { trackingProperties, customClickTrackValue } = getTrackingWithAutoLinkType(
 *   model.tracking,
 *   'planForStayButton',
 *   planForYourStayUrl
 * );
 * <Link trackingProperties={trackingProperties} custom_click_track_value={customClickTrackValue} />
 */
export const getTrackingWithAutoLinkType = (
  tracking: CommonTrackingConfig[] | undefined,
  keyValue: string,
  url: string | undefined
): { trackingProperties: TrackingPropsInterface; customClickTrackValue: string | undefined } => {
  const trackingProperties = getTrackingByKey(tracking, keyValue);
  const customClickTrackValue = getCustomClickTrackValueFromUrl(trackingProperties, url);
  return { trackingProperties, customClickTrackValue };
};
